import { useState, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, popularComics, recommendedComics } from '@/mocks/homeData';
import SeoStructuredData from '@/components/base/SeoStructuredData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const category = categories.find((c) => c.slug === slug);
  const categoryName = category?.name || (slug === 'all' ? '全部漫画' : (slug === 'free' ? '免费漫画' : (slug === 'korean-ranking' ? '韩漫人气榜' : '漫画列表')));
  const categoryDesc = category?.description || '浏览我们精选的漫画资源';

  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular');

  const allComics = [...popularComics, ...recommendedComics];

  const filteredComics = useMemo(() => {
    let comics = allComics;
    if (slug === 'all' || !slug) {
      comics = allComics;
    } else if (slug === 'free') {
      comics = allComics;
    } else if (slug === 'korean-ranking') {
      comics = [...allComics].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    } else if (category) {
      comics = allComics.filter((c) => c.category === slug);
    }
    if (sortBy === 'rating') {
      comics = [...comics].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    } else if (sortBy === 'newest') {
      comics = [...comics].sort((a, b) => (b.id || 0) - (a.id || 0));
    } else {
      comics = [...comics].sort((a, b) => ((b.views ? parseInt(b.views.replace(/[^0-9]/g, '')) : 0) - (a.views ? parseInt(a.views.replace(/[^0-9]/g, '')) : 0)));
    }
    return comics;
  }, [slug, sortBy]);

  const canonicalUrl = `http://thinkbu.com/${location.pathname}`;

  return (
    <>
      <SeoStructuredData type="WebPage" data={{ name: `${categoryName} - 土豪漫画网`, url: canonicalUrl, description: categoryDesc }} />
      <SeoStructuredData
        type="BreadcrumbList"
        data={{
          items: [
            { name: '首页', url: 'http://thinkbu.com/' },
            { name: '漫画分类', url: 'http://thinkbu.com/category/all' },
            { name: categoryName, url: canonicalUrl },
          ],
        }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          {/* Breadcrumb */}
          <nav aria-label="面包屑导航" className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/category/all" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">漫画分类</Link>
            {slug !== 'all' && slug !== 'free' && slug !== 'korean-ranking' && (
              <>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-900 font-medium whitespace-nowrap">{categoryName}</span>
              </>
            )}
            {(slug === 'all' || slug === 'free' || slug === 'korean-ranking') && (
              <>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-900 font-medium whitespace-nowrap">{categoryName}</span>
              </>
            )}
          </nav>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="w-full lg:w-64 shrink-0">
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 lg:sticky lg:top-24">
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-4">漫画分类</h3>
                <div className="space-y-1">
                  <Link
                    to="/category/all"
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                      slug === 'all' ? 'bg-primary-100 text-primary-700 font-medium' : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                    }`}
                  >
                    <span>全部漫画</span>
                    <span className="text-xs text-foreground-400">{allComics.length}部</span>
                  </Link>
                  <Link
                    to="/category/all"
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                      slug === 'free' ? 'bg-primary-100 text-primary-700 font-medium' : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                    }`}
                  >
                    <span>免费漫画</span>
                    <span className="text-xs text-foreground-400">全部免费</span>
                  </Link>
                  <Link
                    to="/category/all"
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                      slug === 'korean-ranking' ? 'bg-primary-100 text-primary-700 font-medium' : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                    }`}
                  >
                    <span>韩漫人气榜</span>
                    <i className="ri-fire-line text-xs text-foreground-400"></i>
                  </Link>
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to="/category/all"
                      className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                        slug === cat.slug ? 'bg-primary-100 text-primary-700 font-medium' : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                      }`}
                    >
                      <span>{cat.name}</span>
                      <span className="text-xs text-foreground-400">{cat.count}部</span>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{categoryName}</h1>
                  <p className="text-foreground-500 text-sm mt-1">{categoryDesc} · 共 {filteredComics.length} 部作品</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">排序：</span>
                  {[
                    { key: 'popular' as const, label: '人气' },
                    { key: 'rating' as const, label: '评分' },
                    { key: 'newest' as const, label: '最新' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        sortBy === opt.key
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comics Grid */}
              {filteredComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {filteredComics.map((comic) => (
                    <Link
                      key={comic.id}
                      to="/category/all"
                      className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-accent-300 transition-all duration-300 cursor-pointer"
                      data-product-shop
                    >
                      <div className="aspect-[3/4] overflow-hidden">
                        <img
                          src={comic.cover}
                          alt={comic.title}
                          title={`${comic.title} - 韩国漫画在线观看`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm font-bold text-foreground-900 mb-1.5 line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {comic.title}
                        </h3>
                        {'tags' in comic && comic.tags && (
                          <div className="flex flex-wrap gap-1 mb-2.5">
                            {comic.tags.slice(0, 3).map((tag: string) => (
                              <span key={tag} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-[10px] font-medium">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-star-fill text-amber-400 text-[10px]"></i>
                            {'rating' in comic ? comic.rating : '9.0'}
                          </span>
                          <span>{'latestChapter' in comic ? comic.latestChapter : '连载中'}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-4xl text-foreground-300 mb-4 block"></i>
                  <p className="text-foreground-500">该分类暂无漫画，请尝试其他分类</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}